import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { resources } from '../../core/services/resources';
import type { PromptSet } from '../../core/types/data-models';

/** Picker only ever shows N options — keep the round-trip cheap. */
const PICKER_PAGE_SIZE = 10;

function truncate(text: string, max: number): string {
  if (!text) return '';
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

function useDebounced<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handle);
  }, [value, delay]);
  return debounced;
}

interface AddPromptSetPanelProps {
  /** Title shown in the accordion header. Default "Add prompt set". */
  title?: string;
  /**
   * Prompt set ids already attached to the parent — filtered out of
   * the picker client-side.
   */
  excludePromptSetIds?: Set<number>;
  /**
   * Fired when the user clicks the Add button. The parent decides what
   * to do (typically: open a per-template overrides dialog, then attach).
   */
  onAdd: (promptSet: PromptSet) => Promise<void> | void;
  /** Label for the add button. */
  addButtonLabel?: string;
  /** Error surfaced from the parent's attach mutation. */
  error?: string | null;
  onErrorClose?: () => void;
  /** Whether an attach is in flight. */
  isAdding?: boolean;
  /** Whether the accordion opens expanded. Defaults to collapsed. */
  defaultExpanded?: boolean;
}

/**
 * Search-and-attach panel for prompt sets. Mirrors the shape of
 * `AddPromptPanel` but with a single free-text filter (name OR
 * description ILIKE on the server) — prompt sets don't have the same
 * FK metadata, so categories/personas/stages/brands filters don't apply.
 */
export function AddPromptSetPanel({
  title = 'Add prompt set',
  excludePromptSetIds,
  onAdd,
  addButtonLabel = 'Add',
  error,
  onErrorClose,
  isAdding,
  defaultExpanded = false,
}: AddPromptSetPanelProps) {
  const [selected, setSelected] = useState<PromptSet | null>(null);
  const [textFilter, setTextFilter] = useState('');
  const debouncedText = useDebounced(textFilter, 300);

  const search = useQuery({
    queryKey: ['prompt-sets', 'picker', { q: debouncedText }],
    queryFn: () =>
      resources.promptSets.list({
        page: 1,
        pageSize: PICKER_PAGE_SIZE,
        sort: '-id',
        filter: debouncedText.trim() ? { q: debouncedText.trim() } : undefined,
      }),
    placeholderData: (prev) => prev,
  });

  const options = (search.data?.data ?? []).filter(
    (s) => !excludePromptSetIds?.has(s.id),
  );

  const handleAdd = async () => {
    if (!selected) return;
    await onAdd(selected);
    setSelected(null);
  };

  return (
    <Accordion
      disableGutters
      defaultExpanded={defaultExpanded}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="add-prompt-set-panel"
        id="add-prompt-set-header"
      >
        <Typography variant="subtitle1" fontWeight={600}>
          {title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails id="add-prompt-set-panel">
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField
            size="small"
            fullWidth
            label="Search by name or description"
            value={textFilter}
            onChange={(e) => setTextFilter(e.target.value)}
            placeholder="Type to filter the catalog"
          />
        </Stack>

        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Autocomplete
            sx={{ flex: 1, minWidth: 320 }}
            options={options}
            value={selected}
            onChange={(_, next) => setSelected(next)}
            getOptionLabel={(s) => s.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            loading={search.isFetching}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.id}>
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {option.name}
                  </Typography>
                  {option.description && (
                    <Typography variant="caption" color="text.secondary">
                      {truncate(option.description, 140)}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Pick a prompt set"
                helperText={
                  options.length === 0 && !search.isFetching
                    ? 'No prompt sets match the current keyword.'
                    : `Showing first ${options.length} match${options.length === 1 ? '' : 'es'} (of up to ${PICKER_PAGE_SIZE}). Narrow the keyword to find more.`
                }
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {search.isFetching ? <CircularProgress size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            disabled={!selected || !!isAdding}
            onClick={handleAdd}
            sx={{ mt: 1 }}
          >
            {isAdding ? 'Adding…' : addButtonLabel}
          </Button>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={onErrorClose}>
            {error}
          </Alert>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
